import { Container, Divider } from "@material-ui/core";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  ButtonGroupTop,
  CurrencyDisplay,
  CustomTable,
  ModalError,
  MonthYearRangePicker,
  Page,
  PrimaryButton,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import {
  downloadFileFromRes,
  formatDate,
  formatPayloadDate,
  getErrors,
  wordCapitalize,
} from "utils";

function DownloadCCID({ userLogin }) {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const { publisher_id } = userLogin?.publisher || {};
  const state = useSelector(data => data?.publisherId);
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const pageTitle = "Download CCID";
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    per_page: 10,
    publisher_id: publisher_id || state?.publisherId || 0,
    dsp_id: "",
    start_date: moment().startOf("year"),
    end_date: moment().startOf("month"),
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [optionDsp, setOptionDsp] = useState([]);
  const [selectedCCID, setSelectedCCID] = useState([]);
  const [optionPublisher, setOptionPublisher] = useState([]);

  const allCCIDSelected = dataTable?.every(option =>
    selectedCCID?.some(selected => selected?.file_path === option?.file_path)
  );
  const roleAdmin = userLogin?.role?.type === "admin";

  const handleSelectAllCCID = event => {
    const { checked } = event?.target || false;
    setSelectedCCID(checked ? dataTable : []);
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleSelectCCID = (event, option) => {
    const { checked } = event?.target || false;
    setSelectedCCID(prev =>
      checked
        ? [...prev, option]
        : prev.filter(item => item.file_path !== option.file_path)
    );
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/ccid/list`, {
        headers,
        params: {
          ...queryParams,
          start_date: formatPayloadDate(queryParams?.start_date),
          end_date: formatPayloadDate(queryParams?.end_date),
        },
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.per_page);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const getOptionDsp = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publishers/list/${queryParams?.publisher_id}/dsps`,
        { headers }
      );
      const { data } = res?.data;
      const modifiedData = data?.map(item => ({
        ...item,
        id: item?.dsp_id,
        label: item?.name,
      }));
      setOptionDsp(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const getOptionPublisher = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/ccid/filter-publishers/${queryParams?.dsp_id}`,
        { headers }
      );
      const { data } = res?.data;
      const modifiedData = data?.map(item => ({
        ...item,
        id: item?.publisher_id,
        label: item?.publisher_name,
      }));
      setOptionPublisher(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleDownloadCCID = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.post(
        `${hardBaseUrl}/ccid/download`,
        {
          file_paths: selectedCCID?.map(item => item?.file_path) || [],
          publisher_id: queryParams?.publisher_id,
        },
        { headers, responseType: "blob" }
      );
      downloadFileFromRes(res);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    getOptionDsp();
  }, []);
  useEffect(() => {
    if (roleAdmin && queryParams?.dsp_id) {
      getOptionPublisher();
    }
  }, [queryParams?.dsp_id]);
  useEffect(() => {
    if (queryParams?.publisher_id && queryParams?.dsp_id) {
      getDataTable();
    }
  }, [queryParams]);

  return (
    <Page className={classes.root} title={pageTitle}>
      <Container maxWidth={false}>
        <HeaderTitle
          title={roleAdmin ? "CCID" : pageTitle}
          breadcrumbData={breadcrumbData({ pageTitle, roleAdmin })}
        />
        {roleAdmin && <ButtonGroupTop />}
        <Divider
          className={roleAdmin ? classes.flatDivider : classes.divider}
        />
        <Grid container columnSpacing={2} justifyContent="right" my={2}>
          <Grid item>
            <AutoCompleteComponent
              label="DSP"
              options={optionDsp}
              value={
                optionDsp.find(option => option.id === queryParams?.dsp_id) ||
                null
              }
              onChange={value => handleChangeQueryParams(value, "dsp_id")}
              size="small"
              width={250}
            />
          </Grid>
          {roleAdmin && (
            <Grid item>
              <AutoCompleteComponent
                label="Publisher"
                options={optionPublisher}
                value={
                  optionPublisher.find(
                    option => option.id === queryParams?.publisher_id
                  ) || null
                }
                onChange={value =>
                  handleChangeQueryParams(value, "publisher_id")
                }
                size="small"
                width={250}
              />
            </Grid>
          )}
          <Grid item>
            <MonthYearRangePicker
              label="Period"
              startDate={queryParams?.start_date}
              handleChangeStartDate={date =>
                handleChangeQueryParams(date, "start_date")
              }
              endDate={queryParams?.end_date}
              handleChangeEndDate={date =>
                handleChangeQueryParams(date, "end_date")
              }
            />
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.per_page}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "per_page")
          }
          isHaveCheckbox={dataTable?.length > 0}
          checkboxHeaderChecked={allCCIDSelected}
          handleCheckboxHeader={handleSelectAllCCID}
          checked={item =>
            selectedCCID?.some(
              selected => selected?.file_path === item?.file_path
            )
          }
          handleCheckbox={handleSelectCCID}
        />
        {selectedCCID?.length > 0 && (
          <Grid container justifyContent="right" mt="16px">
            <PrimaryButton label="Download" onClick={handleDownloadCCID} />
          </Grid>
        )}
      </Container>
    </Page>
  );
}

const breadcrumbData = ({ pageTitle, roleAdmin }) => [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  ...(roleAdmin
    ? [
        {
          label: "CCID",
          link: "/admin/download-ccid",
        },
      ]
    : []),
  {
    label: pageTitle,
    active: true,
  },
];
const columnTable = [
  {
    name: "file_name",
    title: "File Name",
    renderText: item => item || "-",
  },
  {
    name: "traffic",
    title: "Traffic",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
  {
    name: "revenue",
    title: "Revenue",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} prefix="$" decimalScale={2} />
    ),
  },
  {
    name: "date",
    title: "Date",
    renderText: date => formatDate(date),
  },
  {
    name: "file_size",
    title: "File Size",
    renderText: item => item || "-",
  },
  {
    name: "total_song",
    title: "Total Song",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
  {
    name: "total_row",
    title: "Total Row",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
  {
    name: "status",
    title: "Status",
    renderText: status => (
      <StatusChip
        label={
          status === "on_progress"
            ? "On Progress"
            : wordCapitalize(status) || "-"
        }
        type={
          status === "on_progress"
            ? "warning"
            : status === "sent"
            ? "active"
            : status === "confirmed"
            ? "success"
            : "default  "
        }
      />
    ),
  },
];

export default DownloadCCID;
